import React, { useEffect, useState } from 'react';
import styles from './ChartsSection.module.css';
import * as dashboardApi from '../../../../api/dashboardApi';

interface ChartDataPoint {
  month: string;
  [key: string]: string | number; // Dynamic regulation types
}

interface PolicyCategory {
  name: string;
  count: number;
  percentage: number;
}

interface PolicyTrackingChartProps {
  data?: ChartDataPoint[];
  footer?: React.ReactNode;
}

const PolicyTrackingChart: React.FC<PolicyTrackingChartProps> = ({ data, footer }) => {
  const [selectedPoint, setSelectedPoint] = React.useState<{ month: string; data: ChartDataPoint } | null>(null);
  
  if (!data || data.length === 0) {
    return (
      <div className={styles.chart}>
        <h3 className={styles.chartTitle}>Policy Tracking Trends</h3>
        <div style={{ padding: '60px 20px', textAlign: 'center', color: 'var(--ds-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }
  
  const chartData = data;
  
  // Define all possible jurisdiction types with their colors
  const colors: { [key: string]: string } = {
    federal: 'var(--primary-500)',
    state: 'var(--ds-success)',
    country: 'var(--ds-brand-blue)',
    'constituent_country': 'var(--ds-brand-blue-light)',
    supranational: 'var(--ds-danger)'
  };
  
  // Get all jurisdiction types that appear in data
  const dataTypes = Array.from(
    new Set(chartData.flatMap(d => Object.keys(d).filter(k => k !== 'month')))
  );
  
  // Use all defined types, not just the ones in data
  const jurisdictionTypes = Object.keys(colors).filter(type => 
    dataTypes.includes(type) || Object.prototype.hasOwnProperty.call(colors, type)
  ).sort();

  // Get max individual value (not total)
  const maxValue = Math.max(...chartData.flatMap(d => 
    jurisdictionTypes.map(type => (d[type] as number) || 0)
  )) || 1000;
  
  // Calculate nice axis intervals
  const axisInterval = Math.ceil(maxValue / 5 / 10) * 10;
  const roundedMax = axisInterval * 5;
  
  // SVG dimensions - slightly reduced height
  const padding = 30;
  const svgWidth = 550;
  const svgHeight = 210;
  const graphWidth = svgWidth - padding * 2;
  const graphHeight = svgHeight - padding * 2;
  
  // Calculate positions
  const pointSpacing = graphWidth / (chartData.length - 1 || 1);
  
  const getY = (value: number) => padding + graphHeight - (value / roundedMax) * graphHeight;
  const getX = (index: number) => padding + index * pointSpacing;

  // Treat "state" as the primary series for stronger emphasis
  const primaryType = 'state';
  
  // Generate smooth cubic bezier path
  const generatePath = (type: string) => {
    const pts = chartData.map((d, i) => ({
      x: getX(i),
      y: getY((d[type] as number) || 0),
    }));
    if (pts.length === 0) return '';
    let d = `M ${pts[0].x},${pts[0].y}`;
    for (let i = 1; i < pts.length; i++) {
      const cpx = (pts[i - 1].x + pts[i].x) / 2;
      d += ` C ${cpx},${pts[i - 1].y} ${cpx},${pts[i].y} ${pts[i].x},${pts[i].y}`;
    }
    return d;
  };

  // Generate smooth area path (line + closing baseline)
  const generateAreaPath = (type: string) => {
    const line = generatePath(type);
    if (!line) return '';
    const pts = chartData.map((d, i) => ({ x: getX(i) }));
    const baseline = pts.map(p => `${p.x},${getY(0)}`).reverse().join(' L ');
    return `${line} L ${baseline} Z`;
  };

  return (
    <div className={`${styles.chart} ${styles.chartFrameless}`}>
      <h3 className={styles.chartTitle}>Policy Tracking Trends</h3>
      <div style={{ overflowX: 'auto', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <svg width={svgWidth} height={svgHeight} style={{ minWidth: svgWidth }}>
          {/* Grid lines */}
          {Array.from({ length: 5 }).map((_, i) => {
            const y = padding + (graphHeight / 4) * i;
            return (
              <line
                key={`hgrid-${i}`}
                x1={padding}
                y1={y}
                x2={svgWidth - padding}
                y2={y}
                stroke="var(--ds-border)"
                strokeWidth="1"
              />
            );
          })}
          
          {/* Y-axis */}
          <line x1={padding} y1={padding} x2={padding} y2={svgHeight - padding} stroke="var(--ds-text-faint)" strokeWidth="2" />
          
          {/* X-axis */}
          <line x1={padding} y1={svgHeight - padding} x2={svgWidth - padding} y2={svgHeight - padding} stroke="var(--ds-text-faint)" strokeWidth="2" />
          
          {/* Y-axis labels */}
          {Array.from({ length: 5 }).map((_, i) => {
            const value = roundedMax - axisInterval * i;
            const y = padding + (graphHeight / 4) * i;
            return (
              <text key={`ylabel-${i}`} x={padding - 10} y={y + 4} textAnchor="end" fontSize="11" fill="var(--ds-text-muted)">
                {value}
              </text>
            );
          })}
          
          {/* Area layers and lines */}
          {jurisdictionTypes.map((type) => {
            const isPrimary = type.toLowerCase() === primaryType;
            const strokeColor = colors[type.toLowerCase()] || 'var(--ds-text-faint)';
            return (
              <g key={`path-${type}`}>
                {/* Area fill */}
                <path
                  d={generateAreaPath(type)}
                  fill={strokeColor}
                  opacity={isPrimary ? 0.22 : 0.12}
                />
                {/* Line */}
                <path
                  d={generatePath(type)}
                  fill="none"
                  stroke={strokeColor}
                  strokeWidth={isPrimary ? 3 : 2}
                  strokeOpacity={isPrimary ? 1 : 0.7}
                />
              </g>
            );
          })}
          
          {/* Data points */}
          {jurisdictionTypes.map((type) =>
            chartData.map((d, i) => {
              const value = (d[type] as number) || 0;
              const isSelected = selectedPoint?.month === d.month;
              const isPrimaryPoint = type.toLowerCase() === primaryType;
              const pointRadius = isPrimaryPoint ? (isSelected ? 5.5 : 4.5) : isSelected ? 4.2 : 3;
              return (
                <circle
                  key={`point-${type}-${i}`}
                  cx={getX(i)}
                  cy={getY(value)}
                  r={pointRadius}
                  fill={colors[type.toLowerCase()] || 'var(--ds-text-faint)'}
                  stroke="white"
                  strokeWidth="2"
                  style={{ cursor: 'pointer', transition: 'r 0.2s' }}
                  onClick={() => setSelectedPoint({ month: d.month as string, data: d })}
                />
              );
            })
          )}
          
          {/* Invisible larger circles for easier clicking */}
          {chartData.map((d, i) => (
            <circle
              key={`clickarea-${i}`}
              cx={getX(i)}
              cy={padding + graphHeight / 2}
              r="15"
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedPoint({ month: d.month as string, data: d })}
            />
          ))}
          
          {/* X-axis labels */}
          {chartData.map((d, i) => (
            <text
              key={`xlabel-${i}`}
              x={getX(i)}
              y={svgHeight - padding + 18}
              textAnchor="middle"
              fontSize="11"
              fill="var(--ds-text-muted)"
            >
              {d.month}
            </text>
          ))}
        </svg>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center', marginTop: '8px', fontSize: '12px' }}>
        {Object.entries(colors).map(([type, color]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ backgroundColor: color, width: '10px', height: '10px', borderRadius: '2px', display: 'block', flexShrink: 0 }}></span>
            <span style={{ whiteSpace: 'nowrap' }}>{type.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>
          </div>
        ))}
      </div>
      
      {/* Breakdown display when a point is selected */}
      {selectedPoint && (
        <div style={{ 
          marginTop: '12px', 
          padding: '12px 16px', 
          background: 'var(--ds-surface)',
          borderRadius: '14px',
          border: '1px solid var(--ds-border)',
          boxShadow: '0 10px 25px rgba(16, 24, 40, 0.12)',
          color: 'var(--ds-text)'
        }}>
          <div style={{ 
            fontSize: '13px', 
            fontWeight: 600, 
            marginBottom: '10px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline'
          }}>
            <span style={{ color: 'var(--ds-text)' }}>{selectedPoint.month}</span>
            <span style={{ fontSize: '11px', color: 'var(--ds-text-muted)' }}>Regulations by jurisdiction type</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '8px' }}>
            {Object.entries(colors).map(([type, color]) => {
              const count = (selectedPoint.data[type] as number) || 0;
              const displayName = type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
              return (
                <div 
                  key={type} 
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '5px 9px',
                    backgroundColor: 'var(--ds-surface-2)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    border: '1px solid var(--ds-border)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ 
                      backgroundColor: color, 
                      width: '8px', 
                      height: '8px', 
                      borderRadius: '3px', 
                      display: 'block',
                      flexShrink: 0
                    }}></span>
                    <span style={{ color: 'var(--ds-text-secondary)' }}>{displayName}</span>
                  </div>
                  <span style={{ fontWeight: 700, color: 'var(--ds-text)', fontSize: '13px' }}>{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {footer && (
        <div
          style={{
            marginTop: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '12px',
            color: 'var(--ds-text-secondary)',
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
};


interface ChartsSectionProps {
  isLoading?: boolean;
  trends?: any[];
  totalPolicies?: number;
  showSkeletons?: boolean; // when false, suppress large skeleton placeholders
}

export const ChartsSection: React.FC<ChartsSectionProps> = ({ isLoading = false, trends = [], totalPolicies, showSkeletons = true }) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [fullChartData, setFullChartData] = useState<ChartDataPoint[]>([]);
  const [windowEndIndex, setWindowEndIndex] = useState<number | null>(null);
  const [, setCategories] = useState<PolicyCategory[]>([]);
  const [loading, setLoading] = useState(isLoading);

  const processRawTrends = (rawData: any[]) => {
    const transformedChartData: ChartDataPoint[] = rawData.map((point: any) => {
      const chartPoint: ChartDataPoint = { month: point.period };
      const byJurisdictionType = point.by_jurisdiction_type || {};
      Object.entries(byJurisdictionType).forEach(([jurType, count]: [string, any]) => {
        chartPoint[jurType.toLowerCase()] = (count as number) || 0;
      });
      return chartPoint;
    });

    let categoryMap: { [key: string]: number } = {};
    rawData.forEach((t: any) => {
      const byType = t.by_type || {};
      Object.entries(byType).forEach(([name, count]: [string, any]) => {
        const normalizedName = name.toLowerCase();
        categoryMap[normalizedName] = (categoryMap[normalizedName] || 0) + (count as number);
      });
    });

    let categoryData: PolicyCategory[] = Object.entries(categoryMap).map(([name, count]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      count: count as number,
      percentage: 0,
    }));
    const total = categoryData.reduce((sum, cat) => sum + cat.count, 0);
    categoryData = categoryData
      .map(cat => ({ ...cat, percentage: total > 0 ? (cat.count / total) * 100 : 0 }))
      .sort((a, b) => b.count - a.count);

    setFullChartData(transformedChartData);
    const endIndex = transformedChartData.length - 1;
    setChartData(transformedChartData.slice(Math.max(0, endIndex - 5), endIndex + 1));
    setWindowEndIndex(endIndex);
    setCategories(categoryData);
  };

  useEffect(() => {
    // If parent already fetched trends, use them directly — no extra API call
    if (trends && trends.length > 0) {
      processRawTrends(trends);
      setLoading(false);
      return;
    }

    const fetchChartData = async () => {
      try {
        setLoading(true);
        const trendsResponse = await dashboardApi.getTrends({ granularity: 'month' });
        processRawTrends(trendsResponse.data);
      } catch (err) {
        console.error('Failed to fetch chart data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [trends]);

  // Handlers for navigating 6-month windows through the full time series
  const handlePrevWindow = () => {
    if (!fullChartData.length || windowEndIndex === null) return;

    // Move the window 6 points back, but never before the first point
    const newEnd = Math.max(5, windowEndIndex - 6);
    const newStart = Math.max(0, newEnd - 5);

    if (newEnd === windowEndIndex) return; // already at earliest window

    setChartData(fullChartData.slice(newStart, newEnd + 1));
    setWindowEndIndex(newEnd);
  };

  const handleNextWindow = () => {
    if (!fullChartData.length || windowEndIndex === null) return;

    if (windowEndIndex >= fullChartData.length - 1) return; // already at latest window

    const newEnd = Math.min(fullChartData.length - 1, windowEndIndex + 6);
    const newStart = Math.max(0, newEnd - 5);

    setChartData(fullChartData.slice(newStart, newEnd + 1));
    setWindowEndIndex(newEnd);
  };

  if (loading) {
    if (!showSkeletons) return null;

    return (
      <div className={styles.chartsContainer}>
        <div className={`${styles.chart} ${styles.skeleton}`} />
        <div className={`${styles.chart} ${styles.skeleton}`} />
      </div>
    );
  }

  const hasTrendData = chartData.length > 0;
  const canPrev = fullChartData.length > 0 && windowEndIndex !== null && windowEndIndex > Math.min(5, fullChartData.length - 1);
  const canNext = fullChartData.length > 0 && windowEndIndex !== null && windowEndIndex < fullChartData.length - 1;

  const rangeLabel = hasTrendData
    ? `${chartData[0].month as string} – ${chartData[chartData.length - 1].month as string}`
    : '';

  return (
    <div className={styles.chartsContainer}>
      <div>
        <PolicyTrackingChart
          data={chartData}
          footer={
            hasTrendData
              ? (
                  <>
                    <span>
                      Showing&nbsp;
                      <strong>{rangeLabel}</strong>
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={handlePrevWindow}
                        disabled={!canPrev}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '999px',
                          border: '1px solid var(--ds-border)',
                          backgroundColor: canPrev ? 'var(--ds-surface)' : 'var(--ds-surface-2)',
                          color: canPrev ? 'var(--ds-text-secondary)' : 'var(--ds-text-faint)',
                          fontSize: '12px',
                          cursor: canPrev ? 'pointer' : 'default',
                        }}
                      >
                        ◀ Prev 6 months
                      </button>
                      <button
                        onClick={handleNextWindow}
                        disabled={!canNext}
                        style={{
                          padding: '4px 8px',
                          borderRadius: '999px',
                          border: '1px solid var(--ds-border)',
                          backgroundColor: canNext ? 'var(--ds-surface)' : 'var(--ds-surface-2)',
                          color: canNext ? 'var(--ds-text-secondary)' : 'var(--ds-text-faint)',
                          fontSize: '12px',
                          cursor: canNext ? 'pointer' : 'default',
                        }}
                      >
                        Next 6 months ▶
                      </button>
                    </div>
                  </>
                )
              : undefined
          }
        />
      </div>
    </div>
  );
};
